import { useState } from "react";
import { ToastFeedback } from "Components/atoms/ToastFeedback";
import { ConfirmationModal } from "Components/molecules/ConfirmationModal";
import { requestAccountDeletion } from "shared/auth/auth";
import { useAuth } from "shared/contexts/AuthContext";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";
import { getUserKey } from "shared/utils/userKey";
import {
  DeleteAccountButton,
  DeleteAccountSection,
  PrivacyContent,
  PrivacyLink,
} from "./styles";

export function PrivacySettings() {
  const { user } = useAuth();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleRequestDeletion = async () => {
    if (submitting) return;

    setSubmitting(true);
    setFeedback(null);

    try {
      await requestAccountDeletion();
      const userKey = user?.email ? await getUserKey(user.email) : null;
      sendGTMEvent("accountDeletionRequest", {
        platform: getPlatformType(),
        result: "success",
        ...(userKey ? { user_key: userKey } : {}),
      });
      setIsDeleteModalOpen(false);
      setFeedback({
        type: "success",
        message:
          "Solicitação criada. Verifique seu e-mail para continuar o encerramento da conta.",
      });
    } catch (error) {
      sendGTMEvent("accountDeletionRequest", {
        platform: getPlatformType(),
        result: "error",
      });
      setIsDeleteModalOpen(false);
      setFeedback({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Não foi possível solicitar a exclusão da conta.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PrivacyContent>
      <ToastFeedback
        message={feedback?.message ?? ""}
        type={feedback?.type ?? "success"}
        isOpen={Boolean(feedback)}
        onClose={() => setFeedback(null)}
      />

      <PrivacyLink
        href="https://achados.voopter.com.br/privacidade"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          sendGTMEvent("accountPrivacyPolicyClick", {
            platform: getPlatformType(),
            source: "account_privacy",
          })
        }
      >
        Acesse nossa Política de Privacidade
      </PrivacyLink>

      <DeleteAccountSection>
        <h3>Exclusão de Conta</h3>
        <p>
          Esta ação inicia o encerramento da sua conta. Enviaremos um link de
          confirmação para o seu e-mail.
        </p>

        <DeleteAccountButton
          type="button"
          disabled={submitting}
          onClick={() => {
            sendGTMEvent("accountDeletionStart", {
              platform: getPlatformType(),
              source: "account_privacy",
            });
            setIsDeleteModalOpen(true);
          }}
        >
          Excluir minha conta
        </DeleteAccountButton>
      </DeleteAccountSection>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Excluir minha conta"
        confirmLabel="Enviar link de exclusão"
        loadingLabel="Enviando..."
        isLoading={submitting}
        onCancel={() => {
          sendGTMEvent("accountDeletionCancel", {
            platform: getPlatformType(),
            stage: "request",
          });
          setIsDeleteModalOpen(false);
        }}
        onConfirm={() => void handleRequestDeletion()}
      >
        Ao continuar, iniciaremos o processo de encerramento da sua conta e
        enviaremos um link de confirmação para <strong>{user?.email}</strong>.
        Seus dados não serão apagados nesta etapa. Para concluir a exclusão,
        acesse o link recebido no e-mail dentro do prazo informado.
      </ConfirmationModal>
    </PrivacyContent>
  );
}
